import type { PluginDriftEvent } from "@/types/api";

/**
 * Plugin selector-drift telemetry — real data (2026-07-20 alerting audit,
 * finding "Telemetria driftu selektorów ma ZERO żywych konsumentów"). Backed
 * by GET /v1/admin/plugin-drift/recent, which surfaces the
 * `plugin_reactive_fetches` Firestore collection: every allowed reactive
 * fetch means a bundled DOM selector just failed for a real user, so this is
 * the early-warning signal for Gmail/Outlook adapter drift. Was previously a
 * hardcoded MOCK with zero live consumers.
 *
 * Column mapping (backend field -> UI column), since the real schema has no
 * single "fallback depth" field: `totalCount` (cumulative reactive-fetch
 * occurrences for this provider/slot/version bucket) is shown as "Liczba
 * fallbacków" — literally how many times this selector fell back —
 * and `uniqueInstalls60Min` (distinct installs hit in the trailing 60-minute
 * alert window, already computed server-side from the writer's pruned
 * install-hash map) as a separate "Unikalne instalacje" column. Both are
 * real fields, not fabricated ones.
 */
export interface FallbackDepthPanelProps {
  events: PluginDriftEvent[];
  loading: boolean;
  error?: string | null;
  /** Window size in days the events were queried for (echoes the `days` query param). */
  days: number;
}

function formatTimestamp(value: Date): string {
  return Number.isNaN(value.getTime()) ? "—" : value.toLocaleString();
}

export function FallbackDepthPanel({ events, loading, error, days }: FallbackDepthPanelProps) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-zinc-300">
          Drift selektorów pluginu —{" "}
          <code className="rounded bg-zinc-800 px-1 text-xs">plugin_reactive_fetches</code>
        </h3>
        <p className="mt-1 text-xs text-zinc-500">
          Każdy reaktywny odczyt oznacza, że wbudowany selektor DOM zawiódł u realnego użytkownika —
          wczesny sygnał driftu adaptera Gmail/Outlook. Okno: ostatnie {days} dni.
        </p>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <p className="text-sm text-zinc-500">Ładowanie danych driftu…</p>
        </div>
      )}

      {!loading && error && (
        <div
          role="alert"
          className="rounded-lg border border-red-700 bg-red-950 px-4 py-3 text-sm text-red-400"
        >
          Błąd pobierania driftu selektorów: {error}
        </div>
      )}

      {!loading && !error && events.length === 0 && (
        <div
          role="status"
          className="rounded-lg border border-zinc-700 bg-zinc-900 px-6 py-12 text-center"
        >
          <p className="text-sm font-semibold text-zinc-400">Brak driftów w oknie {days} dni</p>
          <p className="mt-1 text-xs text-zinc-600">
            Żaden selektor nie spadł na reaktywny fetch w tym oknie — dobry znak, nie brak danych.
          </p>
        </div>
      )}

      {!loading && !error && events.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-zinc-800 text-left text-zinc-500">
                <th className="sticky left-0 z-10 bg-zinc-900 pb-1 pr-4 font-medium">Provider</th>
                <th className="pb-1 pr-4 font-medium">Selektor / kotwica</th>
                <th className="pb-1 pr-4 font-medium">Wersja pluginu</th>
                <th className="pb-1 pr-4 font-medium">Ostatnio widziano</th>
                <th className="pb-1 pr-4 font-medium text-right">Liczba fallbacków</th>
                <th className="pb-1 pr-4 font-medium text-right">Unikalne instalacje (60 min)</th>
                <th className="pb-1 font-medium">Alert</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr
                  key={`${event.date}-${event.pluginVersion}-${event.provider}-${event.slot}`}
                  className="border-b border-zinc-800/50"
                >
                  <td className="sticky left-0 z-10 bg-zinc-900 py-1 pr-4 text-zinc-300">
                    {event.provider}
                  </td>
                  <td className="py-1 pr-4 text-zinc-400">{event.slot}</td>
                  <td className="py-1 pr-4 font-mono text-zinc-400">{event.pluginVersion}</td>
                  <td className="py-1 pr-4 text-zinc-400">{formatTimestamp(event.lastSeen)}</td>
                  <td className="py-1 pr-4 text-right font-mono text-zinc-300">
                    {event.totalCount}
                  </td>
                  <td className="py-1 pr-4 text-right font-mono text-zinc-300">
                    {event.uniqueInstalls60Min}
                  </td>
                  <td className="py-1">
                    <AlertBadge event={event} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AlertBadge({ event }: { event: PluginDriftEvent }) {
  if (event.alert60MinSentAt) {
    return (
      <span className="rounded bg-red-900/60 px-1.5 py-0.5 text-xs font-semibold text-red-400">
        critical
      </span>
    );
  }
  if (event.alert15MinSentAt) {
    return (
      <span className="rounded bg-amber-900/60 px-1.5 py-0.5 text-xs font-semibold text-amber-400">
        urgent
      </span>
    );
  }
  return <span className="text-zinc-700">—</span>;
}
